import {
  Button,
  Divider,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import NameValueForm from "components/utils/NameValueForm";
import CompaniesChoose from "components/companies/CompaniesChoose";

const ParameterForm = ({ onSubmit, type = "create", initialValues }) => {
  const [parameterForm, setParameterForm] = useState(initialValues);
  const [resetValues, setResetValues] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    type: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    type: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(parameterForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (!parameterForm.companies || parameterForm.companies?.length <= 0) {
      toast.error(strings.validations.companiesRequired);
      return;
    }

    if (parameterForm.type === "list") {
      if (parameterForm.values?.length <= 0) {
        toast.error(strings.values.isRequired);
        return;
      }
      if (parameterForm.values?.some((val) => !val.name || !val.value)) {
        toast.error(strings.values.fieldsMissing);
        return;
      }
    }

    if (!hasErrors)
      onSubmit({
        ...parameterForm,
        companies: parameterForm.companies.map((c) => c.id),
      });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setParameterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeCompanies = (companies) => {
    setParameterForm((prevSt) => ({
      ...prevSt,
      companies,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      type: "",
    });
    setParameterForm(initialValues);
    setResetValues((prev) => !prev);
  };

  const onChangeValues = (values) => {
    setParameterForm((prevSt) => ({
      ...prevSt,
      values: values ?? [],
    }));
  };

  useEffect(() => {
    setParameterForm(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={parameterForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
          <SelectField
            name="type"
            value={parameterForm.type}
            label={<LabelRequired text={strings.fields.type.label} />}
            onChange={onChange}
            errorMessage={errors.type}
            hasError={!!errors.type}
          >
            <option value="">{globalStrings.selectOption}</option>
            {Object.entries(strings.fields.type.options).map((opt) => (
              <option value={opt[0]}>{opt[1]}</option>
            ))}
          </SelectField>
        </Grid>
        {parameterForm.type === "list" && (
          <>
            <NameValueForm
              title={strings.values.title}
              values={parameterForm.values}
              onChange={onChangeValues}
            />
            <Divider className="mt-2" />
          </>
        )}
        <CompaniesChoose
          initCompaniesGranted={initialValues.companies || []}
          onChangeCompaniesGranted={onChangeCompanies}
          resetValues={resetValues}
        />
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={parameterForm.description}
          onChange={onChange}
        ></TextAreaField>
        <Grid
          gap="15px"
          templateColumns={{ base: "1fr", large: "2fr 2fr 2fr" }}
        >
          <TextField
            name="abbreviation"
            label={strings.fields.abbreviation.label}
            value={parameterForm.abbreviation}
            onChange={onChange}
          ></TextField>
          <TextField
            name="unit"
            label={strings.fields.unit.label}
            value={parameterForm.unit}
            onChange={onChange}
          ></TextField>
          <TextField
            name="defaultValue"
            label={strings.fields.defaultValue.label}
            value={parameterForm.defaultValue}
            onChange={onChange}
          ></TextField>
        </Grid>
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      type: {
        label: "Tipo",
        options: {
          date: "Fecha",
          number: "Número",
          text: "Texto",
          list: "Lista",
        },
      },
      description: {
        label: "Descripción",
      },
      abbreviation: {
        label: "Abreviatura",
      },
      unit: {
        label: "Unidad",
      },
      defaultValue: {
        label: "Valor por defecto",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    values: {
      title: "Valores",
      fieldsMissing: "Faltan campos por rellenar en la sección de Valores",
      isRequired: "Debe existir al menos 1 valor",
    },
    validations: {
      incorrectValue: "Valor incorrecto",
      companiesRequired: "Al menos 1 empresa es requerida",
    },
  },
});

export default ParameterForm;
